import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, money } from "@/lib/api";
import { StatusChip, TimerBadge, BureauChip } from "@/components/StatusChip";
import { Card, CardContent } from "@/components/ui/card";
import { Users, FileText, AlertTriangle, CheckCircle2, TrendingUp, DollarSign, ShieldAlert, Mail } from "lucide-react";

const STAGES = [
  { key: "lead", label: "Lead", testid: "pipeline-stage-lead" },
  { key: "onboarded", label: "Onboarded", testid: "pipeline-stage-onboarded" },
  { key: "active", label: "Active Disputes", testid: "pipeline-stage-active" },
  { key: "resolved", label: "Resolved", testid: "pipeline-stage-resolved" },
];

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/staff/dashboard").then((r) => setData(r.data)).catch(() => {}); }, []);

  if (!data) return <div className="py-20 text-center text-slate-400 text-sm">Loading dashboard…</div>;
  const { kpis, mail } = data;

  const KPIS = [
    { label: "Active Clients", value: kpis.active_clients, icon: Users, testid: "kpi-active-clients", tone: "text-blue-700" },
    { label: "Disputes In Flight", value: kpis.disputes_in_flight, icon: FileText, testid: "kpi-disputes-in-flight", tone: "text-indigo-700" },
    { label: "Overdue 30-Day Timers", value: kpis.overdue_timers, icon: AlertTriangle, testid: "kpi-overdue-timers", tone: kpis.overdue_timers > 0 ? "text-red-600" : "text-emerald-600" },
    { label: "Items Resolved", value: `${kpis.resolved_items}/${kpis.total_items}`, icon: CheckCircle2, testid: "kpi-resolved-items", tone: "text-emerald-700" },
    { label: "Fraud Flags Awaiting FTC Report", value: kpis.fraud_flags_awaiting_ftc ?? 0, icon: ShieldAlert, testid: "kpi-fraud-flags", tone: (kpis.fraud_flags_awaiting_ftc ?? 0) > 0 ? "text-rose-600" : "text-emerald-600", to: "/admin/clients?fraud=1", sub: (kpis.fraud_flags_awaiting_ftc ?? 0) > 0 ? `${kpis.fraud_clients_awaiting} client${kpis.fraud_clients_awaiting === 1 ? "" : "s"} need to file at IdentityTheft.gov` : "All flagged items have an FTC report" },
    ...(mail?.live ? [{ label: "Click2Mail Balance", value: money(mail.balance), icon: Mail, testid: "kpi-mail-balance", tone: mail.low ? "text-rose-600" : "text-slate-800", to: "/admin/settings", sub: mail.low ? `Below ${money(mail.letter_cost)} per letter — top up` : `≈ ${mail.letters_affordable} letter${mail.letters_affordable === 1 ? "" : "s"} at ${money(mail.letter_cost)}` }] : []),
    { label: "Collected Revenue", value: money(kpis.collected), icon: DollarSign, testid: "kpi-collected-revenue", tone: "text-slate-800" },
    { label: "Est. MRR", value: money(kpis.mrr), icon: TrendingUp, testid: "kpi-mrr", tone: "text-slate-800" },
  ];

  return (
    <div className="space-y-6" data-testid="staff-dashboard">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Operations Dashboard</h1>
          <p className="text-sm text-slate-500">Pipeline, statutory timers, and revenue at a glance.</p>
        </div>
        <Link to="/admin/disputes" data-testid="goto-disputes-link"
          className="text-sm font-medium text-blue-700 hover:underline">Dispute center →</Link>
      </div>

      {mail?.live && mail.low && (
        <Link to="/admin/settings" className="flex flex-wrap items-center gap-3 rounded-lg border border-rose-300 bg-rose-50 px-4 py-3 text-sm text-rose-800 hover:bg-rose-100 transition-colors duration-150" data-testid="mail-low-balance-banner">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span><strong>Click2Mail balance is {money(mail.balance)}</strong> — below the {money(mail.letter_cost)} needed for the next certified letter{mail.drafted > 0 ? ` (${mail.drafted} drafted letter${mail.drafted === 1 ? "" : "s"} waiting)` : ""}. Add funds at click2mail.com or switch to plain Certified Mail in Settings.</span>
        </Link>
      )}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-8 gap-3">
        {KPIS.map(({ label, value, icon: Icon, testid, tone, to, sub }) => {
          const card = (
            <Card key={testid} data-testid={testid} className={`border-slate-200 h-full ${to ? "hover:border-rose-300 transition-colors duration-150" : ""}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">{label}</span>
                  <Icon className={`h-4 w-4 ${tone}`} />
                </div>
                <div className={`text-2xl font-bold font-heading ${tone}`}>{value}</div>
                {sub && <div className="text-[11px] text-slate-500 mt-1 leading-snug" data-testid={`${testid}-sub`}>{sub}</div>}
              </CardContent>
            </Card>
          );
          return to ? <Link key={testid} to={to} className="block">{card}</Link> : card;
        })}
      </div>

      <div>
        <h2 className="font-heading text-lg font-semibold text-slate-800 mb-3">Client Pipeline</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {STAGES.map(({ key, label, testid }) => (
            <div key={key} data-testid={testid} className="rounded-lg border border-slate-200 bg-white">
              <div className="flex items-center justify-between px-3.5 py-2.5 border-b border-slate-100">
                <span className="text-xs font-mono uppercase tracking-[0.18em] text-slate-500">{label}</span>
                <span className="text-xs font-bold text-slate-700 bg-slate-100 rounded-full px-2 py-0.5">{data.pipeline[key]?.length || 0}</span>
              </div>
              <div className="p-2 space-y-1.5 max-h-64 overflow-y-auto">
                {(data.pipeline[key] || []).map((c) => (
                  <Link key={c.id} to={`/admin/clients/${c.id}`} data-testid={`pipeline-client-${c.id.slice(0, 8)}`}
                    className="block rounded-md border border-slate-100 px-3 py-2 hover:border-blue-300 hover:bg-blue-50/40 transition-colors duration-150">
                    <div className="text-sm font-medium text-slate-800">{c.name}</div>
                    <div className="text-xs text-slate-400">{c.email}</div>
                  </Link>
                ))}
                {(data.pipeline[key] || []).length === 0 && (
                  <div className="text-xs text-slate-400 text-center py-6">No clients</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div className="rounded-lg border border-slate-200 bg-white">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <h3 className="font-heading font-semibold text-slate-800">30-Day FCRA Timers</h3>
            <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-400">15 U.S.C. § 1681i</span>
          </div>
          <div className="divide-y divide-slate-100">
            {data.timers.length === 0 && <div className="px-4 py-8 text-center text-sm text-slate-400">No active bureau clocks.</div>}
            {data.timers.map((d) => (
              <Link key={d.id} to={`/admin/disputes/${d.id}`} className="flex items-center justify-between px-4 py-2.5 hover:bg-slate-50 transition-colors duration-150" data-testid={`timer-row-${d.id.slice(0, 8)}`}>
                <div>
                  <div className="text-sm font-medium text-slate-800">{d.client_name}</div>
                  <div className="flex items-center gap-2 mt-0.5"><BureauChip bureau={d.bureau} /><span className="text-xs text-slate-400">Round {d.round}</span></div>
                </div>
                <TimerBadge daysLeft={d.days_left} status={d.status} />
              </Link>
            ))}
          </div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white">
          <div className="px-4 py-3 border-b border-slate-100">
            <h3 className="font-heading font-semibold text-slate-800">Recent Activity</h3>
          </div>
          <div className="divide-y divide-slate-100 max-h-80 overflow-y-auto">
            {data.audit_feed.map((a) => (
              <div key={a.id} className="px-4 py-2.5">
                <div className="text-sm text-slate-700"><span className="font-medium">{a.actor_name}</span> {a.action.replace(/_/g, " ")}</div>
                <div className="text-xs text-slate-400">{a.detail} · {new Date(a.created_at).toLocaleString()}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
